import { addMilliseconds } from "date-fns"
import { action, computed, makeObservable, observable, runInAction } from "mobx"
import { ERC20__factory } from "../../../generated/evmContract"
import { perMinuteTimerValue } from "../../../stores/TimerValue"
import { isETHCurrency } from "../../../stores/appEnvStore/ETHCurrency"
import AuthStore from "../../../stores/authStore/AuthStore"
import { isERC20TokenNeedToResetAllowanceFirst } from "../../../stores/authStore/MetaMaskModule.service"
import CurrencyStore from "../../../stores/currencyStore/CurrencyStore"
import { Result } from "../../../utils/Result"
import {
  DELAYED,
  SuspenseResource,
  readResource,
  safeReadResource,
  suspenseResource,
} from "../../../utils/SuspenseResource"
import { asyncAction, runAsyncAction } from "../../../utils/asyncAction"
import { TokenInfo } from "../../../utils/models/TokenInfo"
import { assertNever } from "../../../utils/types"
import type { UnitPriceDescription } from "../../Spot/components/SpotForm/UnitPriceDescribe"
import type { WrapBridgeConfirmationData } from "../components/WrapBridgePanel/WrapBridgeConfirmation/WrapBridgeConfirmation"
import {
  BridgeChain,
  isEthBridgeChain,
  isOppositeBridgeChain,
  isSupportedOppositeBridgeChain,
  supportedOppositeBridgeChains,
} from "../types/BridgeChain"
import {
  mapBridgeChainToEthChain,
  wrapBridgeNetworkFromBridgeChain,
  wrapBridgeNetworkToBridgeChain,
} from "../types/BridgeChainHelpers"
import type { WrapBridgeNetwork } from "../types/types"
import { FormError, FormErrorType } from "../types/types"
import { FormData, WrapFormModule } from "./WrapFormModule"
import {
  BridgeCurrency,
  isUnwrappableBridgeCurrency,
  isWrappableBridgeCurrency,
  tokenInfoFromBridgeCurrency,
  tokenInfoToBridgeCurrency,
} from "./utils/BridgeCurrency"

export class WrapFormViewModule {
  constructor(
    private readonly currencyStore: Pick<
      CurrencyStore,
      "getTokenInfo$" | "getPrice$"
    >,
    private readonly authStore: Pick<
      AuthStore,
      "metaMaskModule" | "stxAddress$"
    >,
    private readonly module: WrapFormModule,
  ) {
    makeObservable(this)
  }

  @computed
  get availableETHNetworks$(): WrapBridgeNetwork[] {
    return supportedOppositeBridgeChains.map(c =>
      wrapBridgeNetworkFromBridgeChain(c),
    )
  }

  @asyncAction
  async switchToETHNetwork(
    newNetwork: WrapBridgeNetwork,
    run = runAsyncAction,
  ): Promise<void> {
    const chain = wrapBridgeNetworkToBridgeChain(newNetwork)
    if (chain != null && isSupportedOppositeBridgeChain(chain)) {
      await run(this.module.switchToETHChain(chain))
    }
  }

  @computed get fromNetwork(): WrapBridgeNetwork {
    return wrapBridgeNetworkFromBridgeChain(this.module.fromChain)
  }

  @computed get toNetwork(): WrapBridgeNetwork {
    return wrapBridgeNetworkFromBridgeChain(this.module.toChain)
  }

  private get getAddressMap$(): [fromAddr: string, toAddr: string] {
    if (this.module.fromChain === BridgeChain.Stacks) {
      return [
        this.authStore.stxAddress$,
        this.authStore.metaMaskModule.connectedWalletAddress$,
      ]
    }

    if (
      isOppositeBridgeChain(this.module.fromChain) ||
      this.module.fromChain === BridgeChain.Unknown
    ) {
      return [
        this.authStore.metaMaskModule.connectedWalletAddress$,
        this.authStore.stxAddress$,
      ]
    }

    assertNever(this.module.fromChain)
  }

  @computed get fromAddress$(): string {
    return this.getAddressMap$[0]
  }

  @computed get toAddress$(): string {
    return this.getAddressMap$[1]
  }

  @computed
  get availableFromTokens$(): TokenInfo[] {
    return this.module.fromChainCurrencyCandidates.map(c =>
      this.tokenInfoFromBridgeCurrency$(c),
    )
  }

  @computed get fromToken$(): TokenInfo {
    return this.tokenInfoFromBridgeCurrency$(this.module.fromChainCurrency)
  }

  @computed get fromTokenCountToUSD$(): number {
    return this.unitFromTokenUSD$ * this.module.fromTokenCount.read$
  }

  @computed get bridgingStartedAt$(): Date {
    return new Date(perMinuteTimerValue.value)
  }

  @computed get bridgingEndedAt$(): Date {
    return addMilliseconds(
      this.bridgingStartedAt$,
      this.module.transferProphet.value$.costedMilliseconds,
    )
  }

  @action setFromToken(newToken: TokenInfo): void {
    const currency = tokenInfoToBridgeCurrency(newToken)
    if (currency != null) {
      this.module.setFromChainCurrency(currency)
    }
  }

  @computed get toToken$(): TokenInfo {
    return this.tokenInfoFromBridgeCurrency$(this.module.toChainCurrency)
  }

  @computed get toTokenCountToUSD$(): number {
    return this.unitToTokenUSD$ * this.module.toTokenCount$
  }

  @computed get unitTokenPrices$(): UnitPriceDescription {
    return {
      fromUnitUSD: this.unitFromTokenUSD$,
      fromToExchangeRate: 1,
      toUnitUSD: this.unitToTokenUSD$,
      toFromExchangeRate: 1,
    }
  }

  @computed get wrapFeeToken$(): TokenInfo {
    return this.tokenInfoFromBridgeCurrency$(this.module.wrapFeeCurrency$)
  }

  @computed get feeCountToUSD$(): number {
    return this.module.wrapFeeTokenCount$ * this.unitFeeTokenUSD$
  }

  @observable confirmingFormData:
    | undefined
    | {
        confirmationData: SuspenseResource<WrapBridgeConfirmationData>
        executionFormData: FormData
      }

  @computed get formData$(): Result<
    {
      confirmationData: SuspenseResource<WrapBridgeConfirmationData>
      executionFormData: FormData
    },
    FormError
  > {
    let executionFormData: Pick<
      FormData,
      "fromChain" | "toChain" | "fromCurrency" | "toCurrency"
    >
    if (this.module.fromChain === BridgeChain.Stacks) {
      if (!isUnwrappableBridgeCurrency(this.module.fromChainCurrency)) {
        return Result.error({ type: FormErrorType.UnsupportedSourceToken })
      }
      if (!isWrappableBridgeCurrency(this.module.toChainCurrency)) {
        return Result.error({ type: FormErrorType.UnsupportedTargetToken })
      }

      if (!isSupportedOppositeBridgeChain(this.module.toChain)) {
        return Result.error({ type: FormErrorType.UnsupportedTargetChain })
      }

      executionFormData = {
        fromChain: this.module.fromChain,
        fromCurrency: this.module.fromChainCurrency,
        toChain: this.module.toChain,
        toCurrency: this.module.toChainCurrency,
      }
    } else {
      if (!isSupportedOppositeBridgeChain(this.module.fromChain)) {
        return Result.error({ type: FormErrorType.UnsupportedSourceChain })
      }
      if (this.module.toChain !== BridgeChain.Stacks) {
        return Result.error({ type: FormErrorType.UnsupportedTargetChain })
      }

      if (!isWrappableBridgeCurrency(this.module.fromChainCurrency)) {
        return Result.error({ type: FormErrorType.UnsupportedSourceToken })
      }
      if (!isUnwrappableBridgeCurrency(this.module.toChainCurrency)) {
        return Result.error({ type: FormErrorType.UnsupportedTargetToken })
      }

      executionFormData = {
        fromChain: this.module.fromChain,
        fromCurrency: this.module.fromChainCurrency,
        toChain: this.module.toChain,
        toCurrency: this.module.toChainCurrency,
      }
    }

    if (safeReadResource(suspenseResource(() => this.fromAddress$)) == null) {
      return Result.error({
        type: FormErrorType.SourceWalletNotConnected,
      })
    }

    if (safeReadResource(suspenseResource(() => this.toAddress$)) == null) {
      return Result.error({
        type: FormErrorType.TargetWalletNotConnected,
      })
    }

    if (
      this.module.fromTokenCount.get() == null ||
      this.module.fromTokenCount.read$ === 0
    ) {
      return Result.error({
        type: FormErrorType.SourceTokenAmountIsEmpty,
      })
    }

    if (
      this.module.fromTokenCount.read$ > this.module.fromChainCurrencyBalance$
    ) {
      return Result.error({
        type: FormErrorType.SourceTokenInsufficientBalance,
        token: this.fromToken$,
      })
    }
    if (
      this.module.transferProphet.value$.maxAmount != null &&
      this.module.fromTokenCount.read$ >
        this.module.transferProphet.value$.maxAmount
    ) {
      return Result.error({
        type: FormErrorType.SourceTokenAmountTooLarge,
        token: this.fromToken$,
        max: this.module.transferProphet.value$.maxAmount,
      })
    }

    const minAmount = Math.max(
      this.module.transferProphet.value$.minAmount ?? 0,
      this.module.transferProphet.value$.minFeeAmount,
    )
    if (
      this.module.transferProphet.value$.minAmount != null &&
      this.module.fromTokenCount.read$ < minAmount
    ) {
      return Result.error({
        type: FormErrorType.SourceTokenAmountTooSmall,
        token: this.fromToken$,
        min: minAmount,
      })
    }

    if (
      this.module.fromTokenCount.read$ >
      this.module.fromChainCurrencyAllowance.value$
    ) {
      return Result.error({
        type: FormErrorType.SourceTokenInsufficientAllowance,
        token: this.fromToken$,
        currAllowance: this.module.fromChainCurrencyAllowance.value$,
        minAllowance: this.module.fromTokenCount.read$,
      })
    }

    return Result.ok({
      confirmationData: suspenseResource(() => ({
        direction: this.module.direction,
        fromNetwork: this.fromNetwork,
        fromToken: this.fromToken$,
        fromAmount: this.module.fromTokenCount.read$,
        fromAddress: this.fromAddress$,
        toNetwork: this.toNetwork,
        toToken: this.toToken$,
        toAmount: this.module.toTokenCount$,
        toAddress: this.toAddress$,
        toTokenAmountPerFromToken: this.unitTokenPrices$.fromToExchangeRate,
        fee: this.module.wrapFeeTokenCount$,
        feeRate: this.module.transferProphet.value$.feeRate,
        minFeeAmount: this.module.transferProphet.value$.minFeeAmount,
        feeToken: this.wrapFeeToken$,
        waitingBlocks: this.module.wrapCostedMilliseconds$,
      })),
      executionFormData: {
        ...(executionFormData as any),
        fromAddress: this.fromAddress$,
        fromAmount: this.module.fromTokenCount.read$,
        toAddress: this.toAddress$,
        fee: this.module.wrapFeeTokenCount$,
      },
    })
  }

  @computed get openConfirmModal$(): undefined | (() => void) {
    const formData = this.formData$

    if (formData.type === "error") return

    return action("openConfirmModal", () => {
      this.confirmingFormData = formData.payload
    })
  }
  @action closeConfirmModal(): void {
    this.confirmingFormData = undefined
  }

  @observable showingResetThenApproveModal = false
  @action closeResetThenApproveModal(): void {
    this.showingResetThenApproveModal = false
  }
  async tryToApproveFromCurrency(): Promise<void> {
    const { fromChain, fromChainCurrency } = this.module

    if (!isEthBridgeChain(fromChain)) return
    if (!isETHCurrency(fromChainCurrency)) return

    const ethChain = mapBridgeChainToEthChain(fromChain)

    const fromTokenContractAddr =
      this.authStore.metaMaskModule.getErc20ContractAddr$(
        ethChain,
        fromChainCurrency,
      )

    const endpointContractAddr =
      this.authStore.metaMaskModule.getEndpointContractAddr$(ethChain)

    const erc20 = ERC20__factory.connect(
      fromTokenContractAddr,
      this.authStore.metaMaskModule.signer$,
    )

    if (
      await isERC20TokenNeedToResetAllowanceFirst(
        ethChain,
        fromChainCurrency,
        erc20,
        endpointContractAddr,
        this.authStore.metaMaskModule.connectedWalletAddress$,
        this.module.fromTokenCount.read$,
      )
    ) {
      this.showingResetThenApproveModal = true
    } else {
      await this.module.approveFromCurrency()
    }
  }
  async resetThenApproveFromCurrency(): Promise<void> {
    await this.module.resetThenApproveFromCurrency()
    runInAction(() => {
      this.showingResetThenApproveModal = false
    })
  }

  @computed
  private get unitFromTokenUSD$(): number {
    /**
     * TODO: how to get ERC20 token price
     */
    return readResource(DELAYED())
  }

  @computed
  private get unitToTokenUSD$(): number {
    /**
     * TODO: how to get ERC20 token price
     */
    return readResource(DELAYED())
  }

  @computed
  private get unitFeeTokenUSD$(): number {
    /**
     * TODO: how to get ERC20 token price
     */
    return readResource(DELAYED())
  }

  private tokenInfoFromBridgeCurrency$(currency: BridgeCurrency): TokenInfo {
    return tokenInfoFromBridgeCurrency(currency, c =>
      this.currencyStore.getTokenInfo$(c),
    )
  }
}
